/**
 * HC-CHAT-001 (issue #5).
 *
 * The ticket's original write-up assumed the fix was "skip translation-
 * pending state when the message's detected source language already
 * matches the employer's display language." Verified against the real
 * code and live hire-chat-dev data before implementing (per this task's
 * own Step 3 instruction) — that mechanism doesn't exist:
 *
 *   - No message doc anywhere in the schema (mobile's direct Firestore
 *     writes, chat-functions.ts's sendMessage CF) has a source-language
 *     field. There's nothing to compare against the employer's display
 *     language.
 *   - functions/src/triggers/message-triggers.ts's onMessageSent already
 *     unconditionally attempts an 'en' translation for every message
 *     (`langs.add("en")` runs before any per-user preference check), with
 *     the Gemini prompt explicitly instructed to pass English text through
 *     unchanged. A live sample of 50 recent hire-chat-dev messages found
 *     translations.en populated (often identical to the original text) on
 *     100% of them — the server-side "no en written for English text" bug
 *     described in the ticket does not reproduce against current code.
 *   - A wider sample (399 messages) found the REAL failure mode: ~27%
 *     (107/399) have no `translations` field at all — plain English chat
 *     text with nothing written. onMessageSent only runs on message
 *     *create*; it never re-runs for a pre-existing doc. These read as
 *     messages that predate the current always-translate-to-en behavior
 *     (or, more rarely, a message whose translation call failed) — not a
 *     same-language skip condition to detect up front.
 *
 * So the actual bug is: the client waits on translations.en with no
 * fallback for a message that may simply never receive one (old data, or
 * a failed translation call) — not a same-language case that could be
 * detected in advance. isTranslationPending() below fixes the reproduced
 * failure mode directly: once a message is older than
 * TRANSLATION_TIMEOUT_MS with no translation, stop waiting and fall back
 * to the original text, rather than showing an indefinite pending state.
 */

// Comfortably longer than a normal in-flight Gemini call (observed to
// complete within a few seconds) — short enough that a message that will
// never get a translation doesn't leave the pending indicator showing.
export const TRANSLATION_TIMEOUT_MS = 10000;

function toMillis(value) {
  if (value == null) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  return null;
}

/**
 * @param {{translations?: {en?: string}, timestamp?: unknown}} message
 * @param {number} [now] epoch millis, defaults to Date.now()
 * @returns {boolean} true if the pending/typing indicator should still show
 *   for this message (no translation yet, and not old enough to give up).
 */
export function isTranslationPending(message, now = Date.now()) {
  if (message?.translations?.en) return false;
  const sentAt = toMillis(message?.timestamp);
  // No timestamp yet (e.g. an optimistic pre-server-ack local message) —
  // still genuinely pending, not old enough to judge either way.
  if (sentAt == null) return true;
  return now - sentAt < TRANSLATION_TIMEOUT_MS;
}
